import { makeAutoObservable, runInAction } from "mobx";
import {
  IsNotEmpty,
  IsNumber,
  IsString,
  MinLength,
  validate,
} from "class-validator";
import {
  adminGasStationsCreate,
  adminGasStationsGetOne,
  adminGasStationsUpdate,
} from "@pro-sto/api-client";
import type {
  CreateGasStationDto,
  GasStationResponseDto,
  UpdateGasStationDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";
import {
  DEFAULT_MAP_CENTER,
  FUEL_TYPES,
  parseGasStationField,
  type FuelPriceKey,
  type GasStationStatus,
  type UpdateGasStationWithAdminOverride,
} from "@/pages/GasStationsPage/constants";

export interface FuelFormItem {
  key: FuelPriceKey;
  label: string;
  available: boolean;
  price: string;
}

class GasStationFormDto {
  @IsString()
  @IsNotEmpty({ message: "Название обязательно" })
  @MinLength(2, { message: "Минимум 2 символа" })
  name = "";

  @IsNumber({}, { message: "Укажите широту" })
  lat = DEFAULT_MAP_CENTER.lat;

  @IsNumber({}, { message: "Укажите долготу" })
  lon = DEFAULT_MAP_CENTER.lon;
}

function createDefaultFuels(): FuelFormItem[] {
  return FUEL_TYPES.map(({ key, label }) => ({
    key,
    label,
    available: false,
    price: "",
  }));
}

class GasStationFormStore {
  isOpen = false;
  isEditing = false;
  editUuid: string | null = null;
  isSubmitting = false;
  isLoading = false;
  submitAttempted = false;
  validationErrors: Record<string, string> = {};
  fuelErrors: Partial<Record<FuelPriceKey, string>> = {};

  name = "";
  brand = "";
  address = "";
  osmId = "";
  lat = DEFAULT_MAP_CENTER.lat;
  lon = DEFAULT_MAP_CENTER.lon;
  status: NonNullable<GasStationStatus> = "yes";
  detail = "";
  isActive = true;
  clearAdminOverride = false;
  fuels: FuelFormItem[] = createDefaultFuels();

  constructor() {
    makeAutoObservable(this);
  }

  private resetForm() {
    this.name = "";
    this.brand = "";
    this.address = "";
    this.osmId = "";
    this.lat = DEFAULT_MAP_CENTER.lat;
    this.lon = DEFAULT_MAP_CENTER.lon;
    this.status = "yes";
    this.detail = "";
    this.isActive = true;
    this.clearAdminOverride = false;
    this.fuels = createDefaultFuels();
    this.submitAttempted = false;
    this.validationErrors = {};
    this.fuelErrors = {};
  }

  openCreate = (lat?: number, lon?: number) => {
    this.resetForm();
    this.isEditing = false;
    this.editUuid = null;
    if (lat != null && lon != null) {
      this.lat = lat;
      this.lon = lon;
    }
    this.isOpen = true;
  };

  openEdit = async (uuid: string) => {
    this.resetForm();
    this.isEditing = true;
    this.editUuid = uuid;
    this.isOpen = true;
    this.isLoading = true;

    try {
      const station = await adminGasStationsGetOne({ uuid });
      runInAction(() => {
        this.applyStation(station);
        this.isLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
        this.isOpen = false;
        this.editUuid = null;
      });
      toastStore.showError("Не удалось загрузить заправку");
    }
  };

  private applyStation(station: GasStationResponseDto) {
    this.name = station.name;
    this.brand = parseGasStationField(station.brand);
    this.address = station.address ?? "";
    this.osmId = parseGasStationField(station.osm_id);
    this.lat = station.lat;
    this.lon = station.lon;
    this.status = station.status;
    this.detail = parseGasStationField(station.detail);
    this.isActive = true;
    this.fuels = FUEL_TYPES.map(({ key, label }) => {
      const price = station[key];
      const available = price != null;
      return {
        key,
        label,
        available,
        price: available ? String(price) : "",
      };
    });
  }

  close = () => {
    this.isOpen = false;
    this.isEditing = false;
    this.editUuid = null;
    this.isLoading = false;
    this.isSubmitting = false;
    this.resetForm();
  };

  setName = (value: string) => {
    this.name = value;
  };

  setBrand = (value: string) => {
    this.brand = value;
  };

  setAddress = (value: string) => {
    this.address = value;
  };

  setOsmId = (value: string) => {
    this.osmId = value;
  };

  setStatus = (value: NonNullable<GasStationStatus>) => {
    this.status = value;
  };

  setDetail = (value: string) => {
    this.detail = value;
  };

  setIsActive = (value: boolean) => {
    this.isActive = value;
  };

  setClearAdminOverride = (value: boolean) => {
    this.clearAdminOverride = value;
  };

  setMapPosition = (lat: number, lon: number) => {
    this.lat = lat;
    this.lon = lon;
  };

  setFuelAvailable = (key: FuelPriceKey, available: boolean) => {
    this.fuels = this.fuels.map((fuel) =>
      fuel.key === key
        ? { ...fuel, available, price: available ? fuel.price : "" }
        : fuel,
    );
    if (!available) {
      delete this.fuelErrors[key];
    }
  };

  setFuelPrice = (key: FuelPriceKey, price: string) => {
    this.fuels = this.fuels.map((fuel) =>
      fuel.key === key ? { ...fuel, price } : fuel,
    );
    if (this.fuelErrors[key]) {
      delete this.fuelErrors[key];
    }
  };

  private buildFuelPayload(): Pick<
    CreateGasStationDto,
    FuelPriceKey
  > {
    const payload = {} as Pick<CreateGasStationDto, FuelPriceKey>;
    for (const fuel of this.fuels) {
      if (!fuel.available) {
        payload[fuel.key] = null;
        continue;
      }
      const parsed = Number(fuel.price.replace(",", "."));
      payload[fuel.key] = Number.isFinite(parsed) ? parsed : null;
    }
    return payload;
  }

  private validateFuels(): boolean {
    const errors: Partial<Record<FuelPriceKey, string>> = {};
    for (const fuel of this.fuels) {
      if (!fuel.available) continue;
      const parsed = Number(fuel.price.replace(",", "."));
      if (!fuel.price.trim() || !Number.isFinite(parsed) || parsed <= 0) {
        errors[fuel.key] = "Укажите цену";
      }
    }
    this.fuelErrors = errors;
    return Object.keys(errors).length === 0;
  }

  submit = async (onSuccess?: () => void) => {
    this.submitAttempted = true;

    const dto = Object.assign(new GasStationFormDto(), {
      name: this.name.trim(),
      lat: this.lat,
      lon: this.lon,
    });
    const errors = await validate(dto);
    const validationErrors: Record<string, string> = {};
    for (const error of errors) {
      if (error.constraints) {
        validationErrors[error.property] = Object.values(error.constraints)[0];
      }
    }
    this.validationErrors = validationErrors;

    const fuelsValid = this.validateFuels();
    if (errors.length > 0 || !fuelsValid) return;

    this.isSubmitting = true;
    const fuelPayload = this.buildFuelPayload();
    const basePayload = {
      name: this.name.trim(),
      brand: this.brand.trim() || undefined,
      address: this.address.trim() || undefined,
      osm_id: this.osmId.trim() || undefined,
      lat: this.lat,
      lon: this.lon,
      status: this.status,
      detail: this.detail.trim() || undefined,
      is_active: this.isActive,
      ...fuelPayload,
    };

    try {
      if (this.isEditing && this.editUuid) {
        const adminOverridePayload: UpdateGasStationWithAdminOverride =
          this.clearAdminOverride ? { clear_admin_override: true } : {};
        const updateBody: UpdateGasStationDto = {
          ...basePayload,
          ...adminOverridePayload,
        };
        await adminGasStationsUpdate({
          uuid: this.editUuid,
          requestBody: updateBody,
        });
        toastStore.showSuccess("Заправка обновлена");
      } else {
        await adminGasStationsCreate({
          requestBody: basePayload as CreateGasStationDto,
        });
        toastStore.showSuccess("Заправка создана");
      }

      runInAction(() => {
        this.isSubmitting = false;
        this.close();
      });
      onSuccess?.();
    } catch {
      runInAction(() => {
        this.isSubmitting = false;
      });
      toastStore.showError(
        this.isEditing
          ? "Не удалось обновить заправку"
          : "Не удалось создать заправку",
      );
    }
  };
}

export const gasStationFormStore = new GasStationFormStore();
export type { GasStationFormStore };
